/**
 * Client side of the SC-360 consent cloud functions.
 *
 * Cloud is uploaded to Back4App by hand, so a new bundle can meet a cloud that has none of
 * these functions. Callers treat every failure here as "consent unknown" and carry on; nothing
 * in this module may stand between a user and sign-in.
 */
import { parseCloudRun } from '../services/parseCloud'
import { DEFAULT_WORKSPACE_SETTINGS } from '../config/defaultSettings'
import { MARKETING_EMAIL_WORDING_VERSION } from './consentCopy'
import { MAX_SIGNUP_TERMS_VERSION_LENGTH } from '../utils/googleOAuthClient'
import type {
  AcceptTermsParams,
  AcceptTermsResult,
  ConsentLegal,
  ConsentStatus,
  LegalLinks,
  MarketingEmailPreferenceParams,
  MarketingEmailPreferenceResult,
  SignupConsent,
} from './types'

/** The message cloud throws (with code 142) when the Terms version moved on mid-screen. */
export const TERMS_VERSION_CHANGED = 'TERMS_VERSION_CHANGED'

/**
 * True only for a cloud that does not define the function at all.
 *
 * Parse uses 141 both for that and for any error a cloud function script throws, so the code
 * alone would also match a real failure inside a deployed function. Only the "Invalid
 * function" text tells the two apart.
 */
export function isMissingCloudFunction(err: unknown): boolean {
  const e = err as { code?: unknown; message?: unknown } | null | undefined
  return e?.code === 141 && typeof e.message === 'string' && e.message.includes('Invalid function')
}

export function isTermsVersionChangedError(err: unknown): boolean {
  const e = err as { message?: unknown } | null | undefined
  return typeof e?.message === 'string' && e.message.includes(TERMS_VERSION_CHANGED)
}

/**
 * Works with or without a session. A status read never signs anyone out: the sign-in dialog
 * calls it before there is a session, and the Terms gate fails open on any error instead.
 */
export function getConsentStatus(): Promise<ConsentStatus> {
  return parseCloudRun<ConsentStatus>('getConsentStatus', {}, { handleInvalidSession: false })
}

export function acceptTerms(params: AcceptTermsParams): Promise<AcceptTermsResult> {
  return parseCloudRun<AcceptTermsResult>('acceptTerms', params)
}

export function setMarketingEmailPreference(
  params: MarketingEmailPreferenceParams
): Promise<MarketingEmailPreferenceResult> {
  return parseCloudRun<MarketingEmailPreferenceResult>('setMarketingEmailPreference', params)
}

/**
 * The checkbox value in the shape sign-up, the Google callback and the invite step send, plus
 * the Terms version the notice was shown with. The key is left out when the version is unknown
 * (status failed, old cloud, nothing published): cloud then records no Terms and the Terms
 * screen asks after sign-in. An over-long configured version is treated the same way, because
 * cloud answers 142 to it and that would block every sign-up and invite password step.
 */
export function buildSignupConsent(
  marketingEmailOptIn: boolean,
  termsVersion?: string | null
): SignupConsent {
  return {
    marketingEmailOptIn,
    marketingEmailWordingVersion: MARKETING_EMAIL_WORDING_VERSION,
    ...(typeof termsVersion === 'string' &&
    termsVersion !== '' &&
    termsVersion.length <= MAX_SIGNUP_TERMS_VERSION_LENGTH
      ? { termsVersion }
      : {}),
  }
}

/**
 * Policy URLs from Parse.Config when published, otherwise the platform's own /terms and
 * /privacy pages, which is what the sign-in dialog linked to before SC-360.
 */
export function resolveLegalLinks(
  legal: ConsentLegal | null | undefined,
  platformUrl?: string | null
): LegalLinks {
  const base = platformUrl || DEFAULT_WORKSPACE_SETTINGS.platform_url
  return {
    termsUrl: legal?.termsUrl || `${base}/terms`,
    privacyUrl: legal?.privacyUrl || `${base}/privacy`,
  }
}
